import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';

import { Box, Button, SvgIcon, Typography, Alert } from '@mui/material';

import { AlertDialog, SnakebarToaster } from '@/components';
import { useGetFolderAvailableTypologiesAsync } from '@/features/easy/preFolderQualification/apis';
import {
  stylesErrorMessageBox,
  stylesCloseModalButton,
  stylesErrorMessagesText,
  stylesErrorMessagesTitle,
  stylesFolderTypologyChoices,
} from '@/features/easy/preFolderQualification/withoutRORO/PreFolderQualificationWithoutRORO.styles';
import { FolderTypologyChoice } from '@/features/easy/preFolderQualification/withoutRORO/folderTypologyChoice/components/FolderTypologyChoice';
import { useCreateFolderByTypology } from '@/features/easy/preFolderQualification/withoutRORO/hooks';
import { CloseIcon } from '@/utils/iconSVG';

import { useCancelPreFolderQualification } from '../hooks';

export function PreFolderQualificationWithoutRORO() {
  const { folderId } = useParams();
  const { t } = useTranslation('easy_prefolder');
  const { data: prefolderAvailableTypologies = [] } = useGetFolderAvailableTypologiesAsync({ folderId });

  const { hasErrorMessage, setHasErrorMessage, handleCreateFolderTypology } = useCreateFolderByTypology();

  const handleClose = useCallback(() => {
    setHasErrorMessage(false);
  }, [setHasErrorMessage]);

  const {
    isCancelPreFolderAlertDialogOpen,
    isCancelPreFolderPending,
    cancelPreFolderState,
    handleCancelPreFolder,
    handleAgreeCancelPreFolder,
    handleOnDisagreeCancelPreFolder,
    handleCloseCanclPreFolderToaster,
  } = useCancelPreFolderQualification();

  return (
    <>
      {hasErrorMessage && (
        <Alert severity='error' sx={stylesErrorMessageBox}>
          <Box>
            <Typography sx={stylesErrorMessagesTitle}>{t('easy.prefolder.createFolder.errorMessage.title')}</Typography>
            <Typography sx={stylesErrorMessagesText}>
              <ReactMarkdown>{t('easy.prefolder.createFolder.errorMessage.text')}</ReactMarkdown>
            </Typography>
          </Box>
          <Button variant='text' color='error' sx={stylesCloseModalButton} onClick={handleClose}>
            <SvgIcon color='error' component={CloseIcon} />
          </Button>
        </Alert>
      )}

      <Box sx={stylesFolderTypologyChoices}>
        {prefolderAvailableTypologies.map((prefolderTypology) => (
          <FolderTypologyChoice
            folderId={folderId}
            folderTypology={prefolderTypology}
            key={prefolderTypology.folderTypologyId}
            handleCreateFolderTypology={handleCreateFolderTypology}
          />
        ))}
      </Box>

      <Button
        color='error'
        variant='contained'
        disabled={isCancelPreFolderPending}
        sx={{ maxWidth: '200px', mt: 2, position: 'relative', right: 0 }}
        onClick={handleCancelPreFolder}
      >
        {t('easy.prefolder.cancelPrefolder.label')}
      </Button>

      <AlertDialog
        agreeButtonColor='error'
        open={isCancelPreFolderAlertDialogOpen}
        title={t('easy.prefolder.cancelPreFolder.title.label')}
        content={t('easy.prefolder.cancelPreFolder.content.label')}
        disagreeMessage={t('easy.prefolder.cancelPreFolder.cancel.label')}
        agreeMessage={t('easy.prefolder.cancelPreFolder.agreeMessage.label')}
        onDisagree={handleOnDisagreeCancelPreFolder}
        onAgree={handleAgreeCancelPreFolder}
      />

      {cancelPreFolderState.isToasterOpen && (
        <SnakebarToaster
          isOpen
          autoHideDuration={5000}
          content={cancelPreFolderState.content}
          severity={cancelPreFolderState.severity}
          onClose={handleCloseCanclPreFolderToaster}
        />
      )}
    </>
  );
}
